import { useTranslation } from 'react-i18next';
import { Link as RouterLink, Navigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { Box, Link, Stack, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { weddingPaths } from 'routes/paths';
import IconifyIcon from 'components/base/IconifyIcon';

/**
 * TEMPORARY feature flag gating the multi-event-type chooser.
 *
 * While the new baptism / wedding_baptism flows are still being tested, the
 * chooser stays opt-in: organizers fall back to the original wedding-only
 * create flow (`/couple/create-event`) unless the URL carries `?eventTypes=1`.
 * This lets us validate the new event types in isolation without exposing them
 * to real signups yet.
 *
 * To remove once the new event types are verified: delete this constant + the
 * early-return guard below, leaving the chooser as the default entry point.
 */
const EVENT_TYPE_CHOOSER_FLAG = 'eventTypes';

const isEventTypeChooserEnabled = (search: string): boolean =>
  new URLSearchParams(search).get(EVENT_TYPE_CHOOSER_FLAG) === '1';

type ChooserColor = 'primary' | 'secondary' | 'info';

interface EventTypeChoice {
  id: string;
  title: string;
  description: string;
  icon: string;
  color: ChooserColor;
  path: string;
}

/**
 * Event-type chooser — the first step a new organizer sees (linked from the
 * couple login as "start your journey"). Each card routes to a dedicated
 * create-* flow that fixes its `eventType` (no in-form toggle) and shows its
 * own hero. The current query string (e.g. `?plan=`) is forwarded so the
 * landing-page plan funnel survives the extra step.
 */
const CreateEventChooser = () => {
  const { t } = useTranslation();
  const { search } = useLocation();

  // Flag off → behave exactly like before the chooser existed: send organizers
  // straight to the wedding create flow, forwarding the query string so the
  // landing-page `?plan=` funnel survives the redirect.
  if (!isEventTypeChooserEnabled(search)) {
    return <Navigate to={`${weddingPaths.coupleCreateEvent}${search}`} replace />;
  }

  const choices: EventTypeChoice[] = [
    {
      id: 'wedding',
      title: t('auth.create_event.event_type_wedding'),
      description: t('auth.create_event_chooser.wedding_description'),
      icon: 'mdi:ring',
      color: 'primary',
      path: weddingPaths.coupleCreateEvent,
    },
    {
      id: 'baptism',
      title: t('auth.create_event.event_type_baptism'),
      description: t('auth.create_event_chooser.baptism_description'),
      icon: 'mdi:baby-face-outline',
      color: 'info',
      path: weddingPaths.coupleCreateBaptism,
    },
    {
      id: 'wedding_baptism',
      title: t('auth.create_event.event_type_wedding_baptism'),
      description: t('auth.create_event_chooser.wedding_baptism_description'),
      icon: 'material-symbols:celebration-outline-rounded',
      color: 'secondary',
      path: weddingPaths.coupleCreateWeddingBaptism,
    },
  ];

  return (
    <Stack
      direction="column"
      sx={{
        minHeight: 1,
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', lg: 'center' },
        pt: { xs: 4, lg: 10 },
        pb: 10,
      }}
    >
      <Grid
        container
        sx={{
          maxWidth: '35rem',
          rowGap: 4,
          p: { xs: 3, sm: 5 },
        }}
      >
        <Grid size={12}>
          <Stack direction="column" spacing={1}>
            <Typography variant="h4">{t('auth.create_event_chooser.title')}</Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {t('auth.create_event_chooser.subtitle')}
            </Typography>
          </Stack>
        </Grid>

        <Grid size={12}>
          <Stack direction="column" spacing={2}>
            {choices.map((choice) => (
              <EventTypeCard key={choice.id} choice={choice} search={search} />
            ))}
          </Stack>
        </Grid>

        <Grid size={12}>
          <Stack direction="row" justifyContent="center">
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {t('auth.create_event.existing_user_prefix')}
              <Link component={RouterLink} to={weddingPaths.coupleLogin} sx={{ ml: 1 }}>
                {t('auth.create_event.login_link')}
              </Link>
            </Typography>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

interface EventTypeCardProps {
  choice: EventTypeChoice;
  search: string;
}

const EventTypeCard = ({ choice, search }: EventTypeCardProps) => {
  const { title, description, icon, color, path } = choice;

  return (
    <Box
      component={RouterLink}
      to={`${path}${search}`}
      sx={{
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
      }}
    >
      <Box
        sx={{
          p: { xs: 2.5, sm: 3 },
          borderRadius: 2,
          bgcolor: 'background.elevation1',
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'background.elevation2',
            transform: 'translateY(-2px)',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              bgcolor: `${color}.lighter`,
              color: `${color}.dark`,
            }}
          >
            <IconifyIcon icon={icon} sx={{ fontSize: 26 }} />
          </Box>

          <Stack direction="column" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
              {description}
            </Typography>
          </Stack>

          <Box sx={{ alignSelf: 'center', color: 'text.disabled', flexShrink: 0 }}>
            <IconifyIcon icon="material-symbols:arrow-forward-rounded" sx={{ fontSize: 22 }} />
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default CreateEventChooser;
